import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Decimal,
  dateDuJour,
  formaterDate,
  formaterPourcentage,
  type StatutAllocation,
} from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { api, ErreurApi } from '../api/client';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille, type TonPastille } from '../composants/Pastille';
import { Modale } from '../composants/Modale';
import { Icone } from '../composants/Icone';
import { LienFiche } from '../composants/LienAction';

interface Allocation {
  id: string;
  profilId: string;
  version: number;
  dateEffet: string;
  statut: StatutAllocation;
  commentaire: string | null;
  total: string;
  poidsPoches: { ACTIONS: string; TAUX: string; OPCVM: string };
  profil: { code: string; libelle: string };
  lignes: unknown[];
}

interface Profil {
  id: string;
  code: string;
  libelle: string;
}

export const TON_ALLOCATION: Record<StatutAllocation, TonPastille> = {
  BROUILLON: 'neutre',
  A_VALIDER: 'attente',
  VALIDEE: 'succes',
  REMPLACEE: 'neutre',
  REJETEE: 'refus',
};

export const LIBELLE_ALLOCATION: Record<StatutAllocation, string> = {
  BROUILLON: 'Brouillon',
  A_VALIDER: 'À valider',
  VALIDEE: 'En vigueur',
  REMPLACEE: 'Remplacée',
  REJETEE: 'Rejetée',
};

export function Allocations() {
  const { aRole } = useAuth();
  const naviguer = useNavigate();
  const { donnees, chargement, erreur } = useRessource<Allocation[]>('/allocations');
  const profils = useRessource<Profil[]>('/profils');

  const [ouverte, setOuverte] = useState(false);
  const [profilId, setProfilId] = useState('');
  const [dateEffet, setDateEffet] = useState(dateDuJour());
  const [erreurCreation, setErreurCreation] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const allocations = donnees ?? [];
  const peutCreer = aRole('GERANT', 'RESP_GESTION', 'ADMIN');
  const aValider = allocations.filter((a) => a.statut === 'A_VALIDER').length;
  const profilsSansAllocation = (profils.donnees ?? []).filter(
    (p) => !allocations.some((a) => a.profilId === p.id && a.statut === 'VALIDEE'),
  );

  function ouvrir() {
    setProfilId('');
    setDateEffet(dateDuJour());
    setErreurCreation(null);
    setOuverte(true);
  }

  async function creer() {
    setErreurCreation(null);
    setOccupe(true);
    try {
      // La répartition se modifie sur la fiche : y aller directement.
      const creee = await api.post<{ id: string }>('/allocations', { profilId, dateEffet });
      naviguer(`/allocations/${creee.id}`);
    } catch (e) {
      setErreurCreation(e instanceof ErreurApi ? messageErreur(e) : 'Création impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Allocation>[] = [
    { cle: 'profil', entete: 'Profil', rendu: (a) => a.profil.libelle },
    {
      cle: 'version',
      entete: 'Version',
      num: true,
      rendu: (a) => <LienFiche to={`/allocations/${a.id}`}>v{a.version}</LienFiche>,
    },
    { cle: 'effet', entete: "Date d'effet", num: true, rendu: (a) => formaterDate(a.dateEffet) },
    {
      cle: 'actions',
      entete: 'Actions',
      num: true,
      rendu: (a) => formaterPourcentage(a.poidsPoches?.ACTIONS ?? null),
    },
    {
      cle: 'taux',
      entete: 'Taux',
      num: true,
      rendu: (a) => formaterPourcentage(a.poidsPoches?.TAUX ?? null),
    },
    {
      cle: 'opcvm',
      entete: 'OPCVM',
      num: true,
      rendu: (a) => formaterPourcentage(a.poidsPoches?.OPCVM ?? null),
    },
    {
      cle: 'total',
      entete: 'Total',
      num: true,
      rendu: (a) => (
        <span className={a.total && new Decimal(a.total).eq(100) ? undefined : 'valeur-a-revoir'}>
          {formaterPourcentage(a.total ?? null)}
        </span>
      ),
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (a) => (
        <Pastille ton={TON_ALLOCATION[a.statut]}>{LIBELLE_ALLOCATION[a.statut]}</Pastille>
      ),
    },
  ];

  return (
    <>
      <p className="entete__fil">Gestion</p>
      <div className="entete">
        <h1>Allocations cibles</h1>
        <span className="entete__ref">{allocations.length} versions</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      {aValider > 0 && (
        <BandeauAlerte nature="gestion" titre="Versions en attente de validation">
          {aValider} version(s) attendent une seconde paire d’yeux. À leur mise en vigueur, les
          mandats du profil passeront « à réaligner » : le rebalancing restera à lancer.
        </BandeauAlerte>
      )}

      {profilsSansAllocation.length > 0 && (
        <BandeauAlerte nature="gestion" titre="Profils sans allocation en vigueur">
          {profilsSansAllocation.map((p) => p.libelle).join(', ')} — aucun ordre ne pourra être
          généré pour ces profils tant qu’une allocation n’est pas validée.
        </BandeauAlerte>
      )}

      <Bloc
        titre="Versions"
        meta={chargement ? 'chargement…' : undefined}
        actions={
          peutCreer && (
            <button type="button" className="bouton bouton--icone bouton--compact" onClick={ouvrir}>
              <Icone nom="creer" taille={15} />
              Nouvelle version
            </button>
          )
        }
      >
        <Tableau
          colonnes={colonnes}
          lignes={allocations}
          cle={(a) => a.id}
          vide="Aucune version."
        />
      </Bloc>

      <Modale
        ouverte={ouverte}
        onFermer={() => setOuverte(false)}
        surtitre="Allocation cible"
        titre="Nouvelle version"
      >
        {erreurCreation && (
          <BandeauAlerte nature="refus" titre="Création refusée">
            {erreurCreation}
          </BandeauAlerte>
        )}
        <p className="consigne consigne--tete">
          La nouvelle version copie la version en vigueur du profil. Sa fiche s’ouvre ensuite pour
          en ajuster la répartition.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void creer();
          }}
          noValidate
        >
          <div className="champ">
            <label className="surtitre" htmlFor="profil">
              Profil
            </label>
            <select
              id="profil"
              className="champ__select"
              value={profilId}
              onChange={(e) => setProfilId(e.target.value)}
            >
              <option value="">—</option>
              {(profils.donnees ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.libelle}
                </option>
              ))}
            </select>
          </div>
          <div className="champ">
            <label className="surtitre" htmlFor="effet">
              Date d’effet
            </label>
            <input
              id="effet"
              type="date"
              value={dateEffet}
              onChange={(e) => setDateEffet(e.target.value)}
            />
          </div>
          <div className="modale__actions">
            <button
              type="button"
              className="bouton bouton--fantome"
              onClick={() => setOuverte(false)}
            >
              Annuler
            </button>
            <button className="bouton" type="submit" disabled={occupe || !profilId}>
              {occupe ? 'Création…' : 'Créer la version'}
            </button>
          </div>
        </form>
      </Modale>
    </>
  );
}
